import type {
  AccountingMonth,
  AccountingProcedureCategory,
  OverheadGroups,
  WorkTotals,
} from "@dentacore/shared-types";

import { money, plural } from "./format";
import styles from "./accounting.module.css";

/**
 * What the Yearly and Monthly tabs share (STORY-180b): the three figures, the
 * legend for Expected behind Realized, and the cash amounts as table cells.
 * **Nothing here works a figure out** — it adds months the API sent, and the
 * rules stay in `@dentacore/shared-types/accounting`.
 */

/** The four amounts a month or a year is made of, and what is left. */
export interface Totals {
  moneyIn: number;
  materials: number;
  salaries: number;
  expenses: number;
  profit: number;
}

/** Cash totals, booked and finished work, and unpriced bookings, over some months. */
export interface PeriodTotals extends Totals {
  /** Invoices issued, live ones only (AF-D1), and how many. */
  billed: number;
  billedCount: number;
  overhead: OverheadGroups;
  booked: WorkTotals;
  finished: WorkTotals;
  unpriced: number;
}

const noWork = (): WorkTotals => ({ count: 0, priceCents: 0, materialsCents: 0, profitCents: 0 });

/**
 * Adds months up. The cash amounts only from months that have begun — a month
 * ahead holds zeros there anyway — and the work from every month, because
 * bookings ahead are the point of Expected (ER-D2).
 */
export function sumMonths(months: readonly AccountingMonth[]): PeriodTotals {
  const total: PeriodTotals = {
    moneyIn: 0,
    materials: 0,
    salaries: 0,
    expenses: 0,
    profit: 0,
    billed: 0,
    billedCount: 0,
    overhead: { staff: 0, suppliesLab: 0, occupancy: 0, other: 0 },
    booked: noWork(),
    finished: noWork(),
    unpriced: 0,
  };
  for (const month of months) {
    if (month.started) {
      total.moneyIn += month.moneyIn;
      total.materials += month.materials;
      total.salaries += month.salaries;
      total.expenses += month.expenses;
      total.profit += month.profit;
    }
    total.billed += month.billed;
    total.billedCount += month.billedCount;
    for (const key of ["staff", "suppliesLab", "occupancy", "other"] as const) {
      total.overhead[key] += month.overhead[key];
    }
    for (const key of ["booked", "finished"] as const) {
      total[key].count += month[key].count;
      total[key].priceCents += month[key].priceCents;
      total[key].materialsCents += month[key].materialsCents;
      total[key].profitCents += month[key].profitCents;
    }
    total.unpriced += month.unpriced;
  }
  return total;
}

/** Profit in the good colour, a loss in the warm one — and the sign says it too. */
export function signClass(cents: number): string | undefined {
  return cents < 0 ? styles.loss : styles.profit;
}

/**
 * One booking's worth, as a hover reads it: "23,000 price − 960 materials" —
 * or the price alone without Supply, which has no materials to subtract (QF-D4).
 */
export function workTitle(
  label: string,
  work: WorkTotals,
  currency: string,
  withMaterials = true,
): string {
  const head = `${label} ${money(work.profitCents, currency)} — ${plural(work.count, "appointment")}, ${money(work.priceCents, currency)} price`;
  return withMaterials ? `${head} − ${money(work.materialsCents, currency)} materials` : head;
}

/**
 * Profit, Expected revenue, Realized revenue — the three figures (ER-D1).
 * Profit is null for a period that has not begun: there is no cash in a
 * month still ahead, while there may well be bookings.
 */
export function FigureTiles({
  period,
  totals,
  profitStarted,
  currency,
}: {
  /** "2025 so far", "March 2025". */
  period: string;
  totals: PeriodTotals;
  profitStarted: boolean;
  currency: string;
}): React.JSX.Element {
  const moneyOut = totals.materials + totals.salaries + totals.expenses;
  return (
    <div className={styles.facts} data-testid="accounting-facts">
      <div className={styles.fact}>
        <div className={styles.factKey}>Profit, {period}</div>
        {profitStarted ? (
          <>
            <div
              className={`${styles.factValue} ${signClass(totals.profit) ?? ""}`}
              data-testid="accounting-profit"
            >
              {money(totals.profit, currency)}
            </div>
            <div className={styles.factSub}>
              In {money(totals.moneyIn, currency)} − out {money(moneyOut, currency)}
            </div>
          </>
        ) : (
          <>
            <div className={styles.factValue}>—</div>
            <div className={styles.factSub}>Not started</div>
          </>
        )}
      </div>
      <div className={styles.fact}>
        <div className={styles.factKey}>Expected revenue</div>
        <div className={styles.factValue} data-testid="accounting-expected">
          {money(totals.booked.profitCents, currency)}
        </div>
        <div className={styles.factSub}>
          {plural(totals.booked.count, "booking")}
          {totals.unpriced > 0 && `, ${String(totals.unpriced)} with no procedure`}
        </div>
      </div>
      <div className={styles.fact}>
        <div className={styles.factKey}>Realized revenue</div>
        <div className={styles.factValue} data-testid="accounting-realized">
          {money(totals.finished.profitCents, currency)}
        </div>
        <div className={styles.factSub}>
          {plural(totals.finished.count, "finished appointment")}
        </div>
      </div>
    </div>
  );
}

/** Expected behind, Realized in front — named, so neither is colour alone. */
export function WorkLegend({
  expected = "Expected",
  realized = "Realized",
}: {
  expected?: string;
  realized?: string;
}): React.JSX.Element {
  return (
    <div className={styles.legend} aria-hidden="true">
      <span>
        <i className={`${styles.swatch} ${styles.swatchGhost}`} />
        {expected}
      </span>
      <span>
        <i className={`${styles.swatch} ${styles.swatchProfit}`} />
        {realized}
      </span>
    </div>
  );
}

/** Why a booking adds nothing, said once under the chart rather than hidden (ER-D3). */
export function UnpricedNote({ count }: { count: number }): React.JSX.Element | null {
  if (count === 0) return null;
  return (
    <p className={`${styles.note} ${styles.warmNote}`} data-testid="accounting-unpriced">
      {plural(count, "booking")} {count === 1 ? "has" : "have"} no procedure, so{" "}
      {count === 1 ? "it counts" : "they count"} as zero.
    </p>
  );
}

/**
 * A month's or a year's cash amounts, as five table cells — four without
 * Supply, whose materials Accounting does not show (QF-D4).
 */
export function AmountCells({
  totals,
  currency,
  materials = true,
}: {
  totals: Totals;
  currency: string;
  materials?: boolean;
}): React.JSX.Element {
  return (
    <>
      <td className={styles.right}>{money(totals.moneyIn, currency)}</td>
      {materials && <td className={styles.right}>{money(totals.materials, currency)}</td>}
      <td className={styles.right}>{money(totals.salaries, currency)}</td>
      <td className={styles.right}>{money(totals.expenses, currency)}</td>
      <td className={`${styles.right} ${signClass(totals.profit) ?? ""}`}>
        {money(totals.profit, currency)}
      </td>
    </>
  );
}

/**
 * Booked and finished profit by procedure category, as horizontal bars — a
 * pale track for Expected with Realized solid inside it, longest first.
 * Categories are the clinic's own rows (PG-D1); bookings with no procedure
 * are their own last line, so the lines add up to the month.
 */
export function CategoryBars({
  rows,
  categories,
  currency,
}: {
  rows: AccountingMonth["byProcedureCategory"];
  categories: readonly AccountingProcedureCategory[];
  currency: string;
}): React.JSX.Element {
  if (rows.length === 0) return <p className={styles.blank}>No bookings this month.</p>;
  const names = new Map(categories.map((category) => [category.id, category.name]));
  const top = Math.max(1, ...rows.map((row) => Math.max(row.booked, row.finished)));
  const width = (cents: number): string => `${String(Math.max(0, (cents / top) * 100))}%`;
  return (
    <div className={styles.procBars} data-testid="accounting-categories">
      {rows.map((row) => {
        const name =
          row.categoryId === null ? "No procedure" : (names.get(row.categoryId) ?? "Category");
        return (
          <div
            key={row.categoryId ?? "none"}
            className={styles.procRow}
            title={`${name}: expected ${money(row.booked, currency)}, realized ${money(row.finished, currency)}`}
          >
            <span className={styles.lineLabel}>{name}</span>
            <span className={styles.procTrack} aria-hidden="true">
              <i className={styles.procGhost} style={{ width: width(row.booked) }} />
              <i className={styles.procFill} style={{ width: width(row.finished) }} />
            </span>
            <span className={styles.num}>
              <span className={signClass(row.finished)}>{money(row.finished, currency)}</span>
              <span className={styles.procOf}> of {money(row.booked, currency)}</span>
            </span>
          </div>
        );
      })}
    </div>
  );
}
